import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { FormData, FormSchema, VisitDetail } from '@foundation/shared';
import { LOCATION_FLAG_LABEL_TH, VISIT_OUTCOMES, VISIT_OUTCOME_LABEL_TH } from '@foundation/shared';
import { ApiRequestError, api } from '../lib/api';
import { getCurrentPosition, type Coords } from '../lib/image';
import {
  clearDraft,
  loadDraft,
  saveDraft,
  uploadPhoto,
  type UploadedPhoto,
} from '../lib/upload';
import { formatDate, fullName } from '../lib/format';
import { openNavigation, openTel } from '../lib/liff';
import { FormRenderer } from '../components/FormRenderer';

interface CheckinResponse {
  visitId: string;
  status: string;
  checkedInAt: string;
  distanceFromHomeM: number | null;
  locationFlag: 'ok' | 'far' | 'no_gps' | 'mocked';
  requiresReason: boolean;
  geofenceRadiusM: number;
  hideSensitiveSections: boolean;
  form: { templateId: string; version: number; schema: FormSchema } | null;
}

type Step = 'loading' | 'brief' | 'checking_in' | 'form' | 'submitting' | 'done';

export default function VisitFlow() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('loading');
  const [visit, setVisit] = useState<VisitDetail | null>(null);
  const [checkin, setCheckin] = useState<CheckinResponse | null>(null);
  const [formData, setFormData] = useState<FormData>({});
  const [photos, setPhotos] = useState<Record<string, UploadedPhoto[]>>({});
  const [summary, setSummary] = useState('');
  const [outcome, setOutcome] = useState<string>('');
  const [locationReason, setLocationReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{ score: number | null; nextAt: string | null } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const captureTargetRef = useRef<string | null>(null);
  const coordsRef = useRef<Coords | null>(null);

  /* ------------------------------------------------------------- โหลดงาน */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await api<VisitDetail>(`/visits/${id}`);
        if (cancelled) return;
        setVisit(v);

        if (v.status === 'completed') {
          setStep('done');
          return;
        }

        const draft = loadDraft(id);
        if (draft) {
          setFormData(draft.formData as FormData);
          setSummary(draft.summary ?? '');
          setOutcome(draft.outcome ?? '');
          setLocationReason(draft.locationReason ?? '');
          setPhotos(draft.photos ?? {});
        }

        // เคยเช็คอินแล้ว (เช่นเปิดหน้าใหม่หลังเน็ตหลุด) → ข้ามไปหน้าฟอร์มเลย
        setStep(v.checkedInAt ? 'form' : 'brief');
        if (v.checkedInAt && v.formSchema) {
          setCheckin({
            visitId: v.id,
            status: v.status,
            checkedInAt: v.checkedInAt,
            distanceFromHomeM: v.distanceFromHomeM,
            locationFlag: (v.locationFlag ?? 'no_gps') as CheckinResponse['locationFlag'],
            requiresReason: v.locationFlag !== 'ok',
            geofenceRadiusM: 500,
            hideSensitiveSections: false,
            form: { templateId: v.formTemplateId ?? '', version: v.formVersion ?? 1, schema: v.formSchema },
          });
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiRequestError ? e.message : 'เปิดงานไม่สำเร็จ');
          setStep('brief');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /* --------------------------------------------------- บันทึก draft อัตโนมัติ */

  useEffect(() => {
    if (step !== 'form') return;
    const t = setTimeout(
      () =>
        saveDraft(id, { formData, photos, summary, outcome, locationReason }),
      600,
    );
    return () => clearTimeout(t);
  }, [id, step, formData, photos, summary, outcome, locationReason]);

  /* -------------------------------------------------------------- เช็คอิน */

  const doCheckin = useCallback(async () => {
    setStep('checking_in');
    setError(null);
    try {
      const coords = await getCurrentPosition();
      coordsRef.current = coords;

      const res = await api<CheckinResponse>(`/visits/${id}/checkin`, {
        method: 'POST',
        body: coords
          ? { lat: coords.lat, lng: coords.lng, accuracy: coords.accuracy }
          : {},
      });
      setCheckin(res);
      setStep('form');
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'เช็คอินไม่สำเร็จ');
      setStep('brief');
    }
  }, [id]);

  /* --------------------------------------------------------------- ถ่ายรูป */

  const onCapture = useCallback((fieldKey: string) => {
    captureTargetRef.current = fieldKey;
    fileInputRef.current?.click();
  }, []);

  const onFilePicked = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = ''; // เคลียร์เพื่อให้เลือกไฟล์เดิมซ้ำได้
      const fieldKey = captureTargetRef.current;
      if (!fieldKey || files.length === 0) return;

      setUploading(true);
      setError(null);
      try {
        for (const file of files) {
          const coords = coordsRef.current ?? (await getCurrentPosition());
          const uploaded = await uploadPhoto(file, { refId: id, coords });
          setPhotos((prev) => ({ ...prev, [fieldKey]: [...(prev[fieldKey] ?? []), uploaded] }));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'อัปโหลดรูปไม่สำเร็จ');
      } finally {
        setUploading(false);
      }
    },
    [id],
  );

  const onRemovePhoto = useCallback((fieldKey: string, key: string) => {
    setPhotos((prev) => ({
      ...prev,
      [fieldKey]: (prev[fieldKey] ?? []).filter((p) => p.key !== key),
    }));
  }, []);

  /* --------------------------------------------------------------- ส่งงาน */

  async function submit() {
    setStep('submitting');
    setError(null);
    setFieldErrors({});

    const flat = Object.entries(photos).flatMap(([fieldKey, list]) =>
      list.map((p) => ({
        key: p.key,
        fieldKey,
        takenAt: p.takenAt,
        width: p.width,
        height: p.height,
        sizeBytes: p.sizeBytes,
        sha256: p.sha256,
        ...(p.lat !== undefined ? { lat: p.lat, lng: p.lng } : {}),
      })),
    );

    // ฟิลด์ชนิด photo เก็บ key ไว้ใน formData ด้วย เพื่อให้ validate ฝั่งเซิร์ฟเวอร์ตรวจ min/max ได้
    const payloadForm: FormData = { ...formData };
    for (const [fieldKey, list] of Object.entries(photos)) {
      payloadForm[fieldKey] = list.map((p) => p.key);
    }

    try {
      const res = await api<{
        visitCode: string;
        vulnerabilityScore: number | null;
        nextVisit: { scheduledAt: string } | null;
      }>(`/visits/${id}/submit`, {
        method: 'POST',
        idempotent: true,
        body: {
          formData: payloadForm,
          photos: flat,
          summary: summary || undefined,
          outcome: outcome || undefined,
          locationReason: locationReason || undefined,
        },
      });

      clearDraft(id);
      setResult({
        score: res.vulnerabilityScore,
        nextAt: res.nextVisit?.scheduledAt ?? null,
      });
      setStep('done');
    } catch (e) {
      if (e instanceof ApiRequestError) {
        const details = e.details as { issues?: Array<{ field: string; message: string }> } | undefined;
        if (details?.issues) {
          setFieldErrors(
            Object.fromEntries(details.issues.map((i) => [i.field, i.message])),
          );
          setError('กรอกข้อมูลไม่ครบ — ดูรายการที่ต้องแก้ด้านล่าง');
        } else {
          setError(e.message);
        }
      } else {
        setError('บันทึกไม่สำเร็จ');
      }
      setStep('form');
    }
  }

  /* ----------------------------------------------------------------- UI */

  if (step === 'loading') return <div className="center">กำลังโหลดงาน…</div>;

  if (step === 'done') {
    return (
      <div className="splash">
        <div style={{ fontSize: 44 }}>✅</div>
        <h1 style={{ fontSize: 20, margin: 0 }}>บันทึกเรียบร้อย</h1>
        {result?.score !== null && result?.score !== undefined && (
          <p className="muted">คะแนนความเปราะบางล่าสุด {result.score}/100</p>
        )}
        {result?.nextAt && (
          <p className="muted">นัดครั้งถัดไป {formatDate(result.nextAt)}</p>
        )}
        {!result && visit?.status === 'completed' && (
          <p className="muted">งานนี้ถูกบันทึกไปแล้วเมื่อ {formatDate(visit.submittedAt)}</p>
        )}
        <button className="block" onClick={() => navigate('/today')}>
          กลับหน้างานวันนี้
        </button>
      </div>
    );
  }

  return (
    <div className="screen">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        onChange={(e) => void onFilePicked(e)}
      />

      <div className="topbar">
        <button className="back" onClick={() => navigate('/today')}>
          ← งานวันนี้
        </button>
      </div>

      {visit && (
        <div className="card">
          <div className="list-title" style={{ fontSize: 19 }}>
            {visit.beneficiaryName}
          </div>
          <div className="list-sub mono">{visit.visitCode}</div>
          <div className="list-sub">{visit.address ?? 'ไม่ระบุที่อยู่'}</div>
          <div className="btn-row" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="secondary"
              disabled={!visit.beneficiaryPhone}
              onClick={() => visit.beneficiaryPhone && openTel(visit.beneficiaryPhone)}
            >
              📞 โทร
            </button>
            <button
              type="button"
              className="secondary"
              disabled={visit.lat === null || visit.lng === null}
              onClick={() => visit.lat !== null && visit.lng !== null && openNavigation(visit.lat, visit.lng)}
            >
              🗺️ นำทาง
            </button>
          </div>
        </div>
      )}

      {error && <div className="alert error">{error}</div>}

      {step === 'brief' && (
        <>
          <div className="alert info small">
            กดเช็คอินเมื่อถึงหน้างานแล้ว ระบบจะบันทึกตำแหน่งเพื่อยืนยันการลงพื้นที่
            หากอยู่ไกลจากบ้านผู้รับความช่วยเหลือ ยังบันทึกงานได้แต่ต้องระบุเหตุผล
          </div>
          <button className="block" onClick={() => void doCheckin()}>
            📍 เช็คอินหน้างาน
          </button>
        </>
      )}

      {step === 'checking_in' && <div className="center">กำลังระบุตำแหน่ง…</div>}

      {(step === 'form' || step === 'submitting') && checkin && (
        <>
          <LocationBanner checkin={checkin} />

          {checkin.requiresReason && (
            <div className="field">
              <label htmlFor="reason">เหตุผลที่ตำแหน่งไม่ตรง *</label>
              <textarea
                id="reason"
                value={locationReason}
                onChange={(e) => setLocationReason(e.target.value)}
                placeholder="เช่น พบผู้รับความช่วยเหลือที่โรงพยาบาล / สัญญาณ GPS ไม่ดี"
              />
            </div>
          )}

          {checkin.form ? (
            <FormRenderer
              schema={checkin.form.schema}
              value={formData}
              onChange={setFormData}
              hideSensitive={checkin.hideSensitiveSections}
              photos={photos}
              onCapture={onCapture}
              onRemovePhoto={onRemovePhoto}
              errors={fieldErrors}
              disabled={step === 'submitting'}
            />
          ) : (
            <div className="alert warn small">
              งานนี้ยังไม่ได้ผูกแบบฟอร์ม — บันทึกได้เฉพาะสรุปและภาพถ่าย
            </div>
          )}

          <div className="card">
            <h2>สรุปโดยเจ้าหน้าที่</h2>
            <div className="field">
              <label htmlFor="outcome">ผลการเยี่ยม</label>
              <select
                id="outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                disabled={step === 'submitting'}
              >
                <option value="">— เลือก —</option>
                {VISIT_OUTCOMES.map((o) => (
                  <option key={o} value={o}>
                    {VISIT_OUTCOME_LABEL_TH[o]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="summary">บันทึกเพิ่มเติม</label>
              <textarea
                id="summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                disabled={step === 'submitting'}
                placeholder="สิ่งที่พบหน้างาน ปัญหาที่ต้องติดตาม"
              />
            </div>
          </div>

          {uploading && <div className="alert info small">กำลังอัปโหลดรูป…</div>}

          <button
            className="block"
            disabled={step === 'submitting' || uploading}
            onClick={() => void submit()}
          >
            {step === 'submitting' ? 'กำลังบันทึก…' : 'บันทึกผลการเยี่ยม'}
          </button>

          <p className="muted small" style={{ textAlign: 'center' }}>
            ข้อมูลถูกบันทึกไว้ในเครื่องอัตโนมัติ — ถ้าเน็ตหลุดกลับมาทำต่อได้
          </p>
        </>
      )}
    </div>
  );
}

function LocationBanner({ checkin }: { checkin: CheckinResponse }) {
  if (checkin.locationFlag === 'ok') {
    return (
      <div className="alert success small">
        เช็คอินสำเร็จ · ห่างจากบ้าน {checkin.distanceFromHomeM ?? 0} เมตร
      </div>
    );
  }

  const label = LOCATION_FLAG_LABEL_TH[checkin.locationFlag] ?? checkin.locationFlag;
  const detail =
    checkin.locationFlag === 'far'
      ? `ห่างจากบ้าน ${((checkin.distanceFromHomeM ?? 0) / 1000).toFixed(1)} กม. (เกิน ${checkin.geofenceRadiusM} เมตร)`
      : checkin.locationFlag === 'no_gps'
        ? 'ไม่สามารถระบุตำแหน่งได้'
        : 'ตำแหน่งมีความแม่นยำผิดปกติ';

  return (
    <div className="alert warn small">
      <strong>{label}</strong> — {detail}
      <br />
      บันทึกงานได้ตามปกติ แต่ต้องระบุเหตุผลเพื่อให้หัวหน้าตรวจสอบ
    </div>
  );
}

